import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import AnimatedPage from '../components/AnimatedPage';
import { Calendar, Clock, Tag, ArrowLeft, Share2, Eye, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { trackView } from '../services/viewTracker';
import CommentSection from '../components/CommentSection';
import SEOHead from '../components/SEOHead';

const Post = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewCount, setViewCount] = useState(0);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'posts', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const postData = { id: docSnap.id, ...docSnap.data() };
        setPost(postData);
        setViewCount(postData.views || 0);
        
        // Track view asynchronously
        trackView(id).then((wasNewView) => {
          if (wasNewView) {
            setViewCount((prev) => prev + 1);
          }
        });
      } else {
        toast.error('Post not found');
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      toast.error('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${dateStr}, ${timeStr}`;
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.summary,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="heading-2 mb-4">Post Not Found</h2>
          <Link to="/" className="link-style text-black">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <AnimatedPage>
      <SEOHead
        title={post.title}
        description={post.summary || post.content?.replace(/<[^>]*>/g, '').substring(0, 160)}
        image={post.imageUrl || 'https://raiblogs.vercel.app/logo512.png'}
        url={`https://raiblogs.vercel.app/post/${id}`}
        type="article"
        keywords={post.tags?.join(', ') || 'blog, article'}
        publishedTime={post.createdAt?.toDate().toISOString()}
      />
      <div className="min-h-screen pt-20">
        {/* Back Button */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Link
            to="/"
            className="inline-flex items-center link-style text-gray-600 hover:text-black"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Link>
        </div>

        {/* Hero Image */}
        {post.imageUrl && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="max-w-6xl mx-auto px-4 mb-12"
          >
            <img
              src={post.imageUrl}
              alt={post.title}
              className="w-full h-96 object-cover rounded-2xl shadow-2xl"
            />
          </motion.div>
        )}

        {/* Content */}
        <article className="max-w-4xl mx-auto px-4 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Category */}
            {post.category && (
              <div className="flex items-center mb-4">
                <Tag size={16} className="mr-2 text-gray-600" />
                <span className="text-sm font-body font-medium uppercase tracking-wider text-gray-600">
                  {post.category}
                </span>
              </div>
            )}

            {/* Title */}
            <h1 className="heading-1 mb-6">{post.title}</h1>

            {/* Meta */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-8 border-b border-gray-200">
              <div className="flex flex-wrap items-center gap-4 text-gray-600">
                <div className="flex items-center">
                  <Calendar size={18} className="mr-2" />
                  <span className="font-body text-sm">
                    {formatDate(post.createdAt)}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock size={18} className="mr-2" />
                  <span className="font-body text-sm">
                    {post.readTime || '5'} min read
                  </span>
                </div>
                <div className="flex items-center">
                  <Eye size={18} className="mr-2" />
                  <span className="font-body text-sm">
                    {viewCount}
                  </span>
                </div>
                <div className="flex items-center">
                  <MessageCircle size={18} className="mr-2" />
                  <span className="font-body text-sm">
                    {post.commentCount || post.comments?.length || 0}
                  </span>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleShare}
                className="flex items-center justify-center px-6 py-2.5 bg-black text-white rounded-lg font-body font-medium hover:bg-gray-800 transition-colors self-start"
              >
                <Share2 size={18} className="mr-2" />
                Share
              </motion.button>
            </div>

            {/* Summary */}
            {post.summary && (
              <div className="mb-8 p-6 bg-gray-50 rounded-xl border-l-4 border-black">
                <p className="body-text text-gray-700 italic">{post.summary}</p>
              </div>
            )}

            {/* Content */}
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="font-body font-semibold mb-4">Tags</h3>
                <div className="flex flex-wrap gap-3">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-black text-white text-sm font-body rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Author Section */}
            <div className="mt-12 p-8 bg-black text-white rounded-2xl">
              <div className="flex items-center">
                <img 
                  src="https://i.postimg.cc/jq3ZZwSS/raiblogs-rai.jpg" 
                  alt="Rai Mohan" 
                  className="w-16 h-16 rounded-full mr-6 flex-shrink-0 object-cover"
                />
                <div>
                  <h3 className="font-display font-semibold text-xl mb-2">
                    Rai Mohan
                  </h3>
                  <p className="text-gray-300 font-body text-sm">
                    Writer, Developer, and Creative Thinker
                  </p>
                </div>
              </div>
            </div>

            {/* Comment Section */}
            <CommentSection postId={id} />
          </motion.div>
        </article>
      </div>
    </AnimatedPage>
  );
};

export default Post;
